import { Tag, Tooltip } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { ColumnGroup } from 'components/UI/Responsive/ColumnVisibilityDropdown';
import Monetary from 'components/UI/Data/Monetary';
import ShippingFee from 'components/UI/Data/ShippingFee';
import { TFunction } from 'i18next';
import { Order, StatusType } from 'model/Order';
import { dateUtils } from 'utils/date';
import { roundDecimals } from 'utils/util';
import { statusMap } from './maps/status';

const twoLineTitle = (translate: TFunction, top: string, bottom: string) => (
  <>
    {translate(`myOrders.orderType.table.${top}`)} <br />{' '}
    {translate(`myOrders.orderType.table.${bottom}`)}
  </>
);

export const MY_ORDERS_COLUMN_GROUPS: ColumnGroup[] = [
  {
    label: 'Identificação',
    columns: [
      { key: 'status', label: 'Status' },
      { key: 'accountCode', label: 'Chave' },
      { key: 'orderNumber', label: 'Pedido / GFP' },
      { key: 'saleType', label: 'Tipo de compra' },
      { key: 'siteName', label: 'Produtor' },
    ],
  },
  {
    label: 'Datas',
    columns: [
      { key: 'saleConclusion', label: 'Data de compra' },
      { key: 'deliveryDate', label: 'Data de entrega' },
      { key: 'billingDate', label: 'Data de faturamento' },
    ],
  },
  {
    label: 'Produto',
    columns: [
      { key: 'productBarCode', label: 'Código de barras' },
      { key: 'description', label: 'Produto' },
      { key: 'quality', label: 'Qld' },
      { key: 'packingName', label: 'Embalagem' },
      { key: 'packingQuantity', label: 'QE x QpE' },
    ],
  },
  {
    label: 'Valores',
    columns: [
      { key: 'unitPrice', label: 'Valor unitário / total' },
      { key: 'shippingFee', label: 'Frete unitário / total' },
      { key: 'unitPriceConsolidated', label: 'Valor total unitário / total' },
      { key: 'voucherAmountLabel', label: 'Adicional / desconto' },
    ],
  },
  {
    label: 'Outros',
    columns: [
      { key: 'userCartInclusion', label: 'Usuário' },
      { key: 'observation', label: 'Observação' },
    ],
  },
];

export const MY_ORDERS_COLUMN_KEYS = MY_ORDERS_COLUMN_GROUPS.flatMap((group) =>
  group.columns.map((column) => column.key),
);

export const groupMyOrdersColumns = (
  columns: ColumnType<Order>[],
  visibleKeys: string[],
): ColumnsType<Order> =>
  MY_ORDERS_COLUMN_GROUPS.map((group) => ({
    key: group.label,
    title: group.label,
    children: group.columns
      .filter((column) => visibleKeys.includes(column.key))
      .flatMap((column) => columns.filter(({ key }) => key === column.key)),
  })).filter((group) => group.children.length > 0);

export const buildMyOrdersColumns = (
  translate: TFunction,
): ColumnType<Order>[] => [
  {
    title: translate('myOrders.orderType.table.status'),
    dataIndex: 'status',
    key: 'status',
    align: 'left',
    render: (status: StatusType, order: Order) => {
      if (statusMap[status]) {
        const { color, description } = statusMap[status];
        return (
          <Tooltip title={order.resultMessage}>
            <Tag color={color}>{description}</Tag>
          </Tooltip>
        );
      }
      return status;
    },
  },
  {
    title: translate('myOrders.orderType.table.key'),
    dataIndex: 'accountCode',
    key: 'accountCode',
    align: 'left',
    sorter: (a: Order, b: Order) => a.accountCode - b.accountCode,
  },
  {
    title: twoLineTitle(translate, 'order', 'gfp'),
    dataIndex: 'orderNumber',
    key: 'orderNumber',
    align: 'left',
    sorter: (a: Order, b: Order) => a.orderNumber - b.orderNumber,
    render: (orderNumber: string, order: Order) => (
      <>
        {orderNumber} <br /> {order.gfpNumber === 0 ? '-' : order.gfpNumber}
      </>
    ),
  },
  {
    title: translate('myOrders.orderType.table.saleType'),
    dataIndex: 'saleType',
    key: 'saleType',
    align: 'left',
    render: (saleType: string) => (saleType === 'VOL' ? 'ENP' : saleType),
  },
  {
    title: translate('myOrders.orderType.table.orderDate'),
    dataIndex: 'saleConclusion',
    key: 'saleConclusion',
    align: 'left',
    defaultSortOrder: 'descend',
    sorter: (actual: Order, next: Order) =>
      dateUtils.orderDate(actual.saleConclusion, next.saleConclusion),
    render: (saleConclusion: string) =>
      dateUtils.formatDate(saleConclusion, 'DD/MM/YYYY HH:mm'),
  },
  {
    title: translate('myOrders.orderType.table.deliveryDate'),
    dataIndex: 'deliveryDate',
    key: 'deliveryDate',
    align: 'left',
    sorter: (actual: Order, next: Order) => {
      if (actual.isLKPSale) {
        return dateUtils.getDateStringNumber(actual.deliveryDate);
      }
      if (next.isLKPSale) {
        return dateUtils.getDateStringNumber(next.deliveryDate);
      }
      return dateUtils.orderDate(actual.deliveryDate, next.deliveryDate);
    },
    render: (deliveryDate: string, order: Order) => {
      if (!order.isLKPSale) {
        return dateUtils.formatDate(deliveryDate, 'DD/MM/YYYY');
      }
      return '-';
    },
  },
  {
    title: translate('myOrders.orderType.table.billingDate'),
    dataIndex: 'billingDate',
    key: 'billingDate',
    sorter: (actual: Order, next: Order) =>
      dateUtils.orderDate(actual.billingDate, next.billingDate),
    align: 'left',
    render: (billingDate: string) =>
      dateUtils.formatDate(billingDate, 'DD/MM/YYYY'),
  },
  {
    title: twoLineTitle(translate, 'producer', 'producerCode'),
    dataIndex: 'siteName',
    key: 'siteName',
    align: 'left',
    render: (siteName: string, order: Order) => (
      <>
        {siteName} <br /> {order.siteCode}
      </>
    ),
  },
  {
    title: translate('myOrders.orderType.table.barcode'),
    dataIndex: 'productBarCode',
    key: 'productBarCode',
    align: 'left',
    sorter: (a: Order, b: Order) => a.productBarCode - b.productBarCode,
  },
  {
    title: twoLineTitle(translate, 'product', 'productCode'),
    dataIndex: 'description',
    key: 'description',
    align: 'left',
    sorter: (a: Order, b: Order) => a.description.localeCompare(b.description),
    render: (description: string, order: Order) => (
      <>
        {description}
        {order.conceptName && (
          <>
            <br />
            <span style={{ fontWeight: 700, color: '#262626' }}>
              {order.conceptName.toUpperCase()}
            </span>
          </>
        )}
        <br /> {order.productCode}
      </>
    ),
  },
  {
    title: translate('myOrders.orderType.table.quality'),
    dataIndex: 'quality',
    key: 'quality',
    align: 'left',
  },
  {
    title: twoLineTitle(translate, 'embValue', 'valueEmb'),
    dataIndex: 'packingName',
    key: 'packingName',
    align: 'left',
    render: (packingName: string, order: Order) => (
      <>
        {packingName}
        <br /> <Monetary symbol value={order.packingPrice} />{' '}
      </>
    ),
  },
  {
    title: twoLineTitle(translate, 'qtdUnit', 'totalUnit'),
    dataIndex: 'packingQuantity',
    key: 'packingQuantity',
    align: 'left',
    render: (packingQuantity: number, order: Order) => (
      <>
        {packingQuantity} x {order.unitPerPacking} <br /> {order.unitQuantity}
      </>
    ),
  },
  {
    title: twoLineTitle(translate, 'vlrUnit', 'vlrTotal'),
    dataIndex: 'unitPrice',
    key: 'unitPrice',
    align: 'left',
    render: (unitPrice: number, order: Order) => (
      <>
        <Monetary symbol value={roundDecimals(unitPrice, 6)} /> <br />
        <Monetary symbol value={order.subTotal} />
      </>
    ),
  },
  {
    title: twoLineTitle(translate, 'shippingUnit', 'shippingTotal'),
    dataIndex: 'shippingFee',
    key: 'shippingFee',
    align: 'left',
    render: (shippingFee: number, order: Order) => (
      <>
        <ShippingFee value={roundDecimals(shippingFee ?? 0.0, 6)} />
        <br />
        <ShippingFee
          value={roundDecimals(shippingFee * order.unitQuantity, 6)}
        />
      </>
    ),
  },
  {
    title: twoLineTitle(translate, 'vlrUnitTotal', 'vlrTotal'),
    dataIndex: 'unitPriceConsolidated',
    key: 'unitPriceConsolidated',
    align: 'left',
    render: (unitPriceConsolidated: number, order: Order) => (
      <>
        <Monetary
          symbol
          value={roundDecimals(unitPriceConsolidated + order.shippingFee, 6)}
        />
        <br />
        <Monetary
          symbol
          value={roundDecimals(
            order.total + order.shippingFee * order.unitQuantity,
            2,
          )}
        />
      </>
    ),
  },
  {
    title: translate('myOrders.orderType.table.addDisc'),
    dataIndex: 'voucherAmountLabel',
    key: 'voucherAmountLabel',
    align: 'left',
  },
  {
    title: translate('myOrders.orderType.table.user'),
    dataIndex: 'userCartInclusion',
    key: 'userCartInclusion',
    align: 'left',
    render: (userCartInclusion: string, order: Order) => (
      <Tooltip title={`${order.accountCode} - ${order.accountName}`}>
        {userCartInclusion}
      </Tooltip>
    ),
  },
  {
    title: translate('myOrders.orderType.table.observation'),
    dataIndex: 'observation',
    key: 'observation',
    align: 'left',
  },
];
